import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Camera, CheckCircle2, ChevronDown, ChevronUp, ClipboardCheck, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/api/client";
import { evaluateChecklist } from "@/lib/auditEngine";
import { compressImage, currentPosition, deviceId, enqueueAnswer, enqueueEvidence, extFor, getAudit, newId, pendingCount, putAudit } from "@/lib/auditStore";
import SignaturePad from "./SignaturePad";
import { useSync } from "./SyncContext";

const BIG = "h-14 min-w-[5.5rem] flex-1 rounded-xl border-2 px-3 text-base font-semibold active:scale-[0.98] transition";
const on = "border-teal-800 bg-teal-800 text-white";
const off = "border-slate-300 bg-white text-slate-900";

// ---------------------------------------------------------------- controllo di risposta per tipo di domanda
function AnswerControl({ q, item, disabled, onChange }) {
  const st = item?.state;
  const set = (patch) => onChange(patch);
  if (q.type === "yes_no_unknown") {
    return (
      <div className="flex flex-wrap gap-2" role="group" aria-label="Risposta">
        {[["yes", "Sì"], ["no", "No"], ["unknown", "Non so"], ["not_applicable", "N.A."]].map(([v, l]) => (
          <button key={v} type="button" disabled={disabled} className={`${BIG} ${st === v ? on : off}`} onClick={() => set({ state: v, value: null })} data-testid={`ans-${v}`}>{l}</button>
        ))}
      </div>
    );
  }
  if (q.type === "single_choice") {
    return (
      <div className="flex flex-col gap-2">
        {q.options.map((o) => (
          <button key={o.value} type="button" disabled={disabled} className={`${BIG} text-left ${st === "answered" && item.value === o.value ? on : off}`} onClick={() => set({ state: "answered", value: o.value })}>{o.label}</button>
        ))}
      </div>
    );
  }
  if (q.type === "multi_choice" || q.type === "equipment_multi") {
    const cur = Array.isArray(item?.value) ? item.value : [];
    const toggle = (v) => {
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      set(next.length ? { state: "answered", value: next } : { state: "unknown", value: null });
    };
    return (
      <div className="flex flex-col gap-2">
        {q.options.map((o) => (
          <button key={o.value} type="button" disabled={disabled} className={`${BIG} text-left ${cur.includes(o.value) ? on : off}`} onClick={() => toggle(o.value)}>{cur.includes(o.value) ? "✓ " : ""}{o.label}</button>
        ))}
        <button type="button" disabled={disabled} className={`${BIG} ${st === "not_applicable" ? on : off}`} onClick={() => set({ state: "not_applicable", value: null })}>Nessuna / N.A.</button>
      </div>
    );
  }
  if (q.type === "number" || q.type === "duration_min") {
    return (
      <input type="number" inputMode="decimal" min="0" disabled={disabled} className="h-14 w-full rounded-xl border-2 border-slate-300 px-4 text-xl" placeholder={q.type === "duration_min" ? "minuti" : "numero"}
        value={st === "answered" && item.value !== null ? item.value : ""} onChange={(e) => (e.target.value === "" ? set({ state: "unknown", value: null }) : set({ state: "answered", value: Number(e.target.value) }))} />
    );
  }
  return (
    <textarea disabled={disabled} maxLength={500} rows={3} className="w-full rounded-xl border-2 border-slate-300 p-3 text-base" placeholder="Testo breve (max 500 caratteri)"
      value={st === "answered" ? item.value || "" : ""} onChange={(e) => (e.target.value.trim() ? set({ state: "answered", value: e.target.value }) : set({ state: "unknown", value: null }))} />
  );
}

// ---------------------------------------------------------------- pagina
export default function AuditorChecklist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { online, refresh, sync, subscribe } = useSync();
  const [rec, setRec] = useState(null);
  const [answers, setAnswers] = useState({});
  const [draftEv, setDraftEv] = useState({});
  const [sigs, setSigs] = useState({ auditor: false, referente: false });
  const [previews, setPreviews] = useState({});
  const [openNote, setOpenNote] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [geoOn, setGeoOn] = useState(() => localStorage.getItem("dvr_geo") === "1");
  const [refName, setRefName] = useState("");
  const dev = useRef(deviceId());
  const fileRef = useRef(null);
  const photoFor = useRef(null);

  const persist = useCallback(async (next) => { await putAudit(next); }, []);

  // carico: prima l'archivio del dispositivo (funziona offline), poi il server se non ci sono modifiche in sospeso
  const load = useCallback(async () => {
    const local = await getAudit(id).catch(() => null);
    if (local) { setRec(local); setAnswers(local.answers || {}); setDraftEv(local.draftEvidence || {}); setSigs(local.signatures || {}); }
    if (!navigator.onLine) return;
    if ((await pendingCount(id)) > 0) return;
    try {
      const d = await apiRequest(`/auditor/audits/${id}`);
      const merged = { ...d, id, draftEvidence: local?.draftEvidence || {}, signatures: local?.signatures || {}, savedAt: new Date().toISOString() };
      const hasSig = d.evidence.some((e) => e.kind === "signature" && e.signer === "auditor");
      merged.signatures = { ...merged.signatures, auditor: merged.signatures.auditor || hasSig };
      setRec(merged); setAnswers(merged.answers); setDraftEv(merged.draftEvidence); setSigs(merged.signatures);
      await putAudit(merged);
    } catch (e) { if (!local) toast.error(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((res) => { if (res && res.ok && res.conflicts.length) load(); }), [subscribe, load]);

  const bundle = rec?.bundle;
  const readonly = !!rec?.audit?.readonly;
  const ev = useMemo(() => (bundle ? evaluateChecklist(bundle, answers) : null), [bundle, answers]);

  const grouped = useMemo(() => {
    if (!ev) return [];
    const g = {};
    ev.visible.forEach((k) => { const t = ev.meta[k].task || "_"; (g[t] = g[t] || []).push(k); });
    const order = ["_", ...bundle.task_order];
    return order.filter((t) => g[t]).map((t) => ({ task: t, name: t === "_" ? "Domande generali" : bundle.tasks[t].name, keys: g[t] }));
  }, [ev, bundle]);

  const total = ev ? ev.visible.filter((k) => bundle.questions[ev.meta[k].question].required).length : 0;
  const done = ev ? ev.visible.filter((k) => bundle.questions[ev.meta[k].question].required && answers[k]).length : 0;

  const save = async (key, patch) => {
    const m = ev.meta[key];
    const prev = answers[key] || {};
    const item = { ...prev, ...patch, question_code: m.question, task_code: m.task, equipment_code: m.equipment, ts: new Date().toISOString(), device: dev.current,
      evidence_ids: [...new Set([...(prev.evidence_ids || []), ...(draftEv[key] || [])])] };
    const nextAnswers = { ...answers, [key]: item };
    setAnswers(nextAnswers);
    await persist({ ...rec, answers: nextAnswers, draftEvidence: draftEv, signatures: sigs });
    await enqueueAnswer(id, key, item);
    await refresh();
    if (navigator.onLine) sync();
  };

  const takePhoto = (key) => { photoFor.current = key; fileRef.current.click(); };
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    const key = photoFor.current;
    if (!file || !key) return;
    const m = ev.meta[key];
    const blob = await compressImage(file);
    const uuid = newId();
    const geo = geoOn ? await currentPosition() : null;
    await enqueueEvidence(id, uuid, blob, { kind: "photo", question_code: m.question, task_code: m.task, equipment_code: m.equipment, taken_at: new Date().toISOString(), geo, filename: `foto-${uuid.slice(0, 8)}.${extFor(blob)}` });
    setPreviews((p) => ({ ...p, [key]: [...(p[key] || []), URL.createObjectURL(blob)] }));
    const nextDraft = { ...draftEv, [key]: [...(draftEv[key] || []), uuid] };
    setDraftEv(nextDraft);
    const existing = answers[key];
    if (existing) {
      const item = { ...existing, evidence_ids: [...new Set([...(existing.evidence_ids || []), uuid])], ts: new Date().toISOString(), device: dev.current };
      const nextAnswers = { ...answers, [key]: item };
      setAnswers(nextAnswers);
      await putAudit({ ...rec, answers: nextAnswers, draftEvidence: nextDraft, signatures: sigs });
      await enqueueAnswer(id, key, item);
    } else {
      await putAudit({ ...rec, answers, draftEvidence: nextDraft, signatures: sigs });
    }
    await refresh();
    if (navigator.onLine) sync();
    toast.success("Foto salvata sul dispositivo");
  };

  const saveSignature = async (who, blob) => {
    await enqueueEvidence(id, newId(), blob, { kind: "signature", signer: who, filename: `firma-${who}.png`, taken_at: new Date().toISOString() });
    const next = { ...sigs, [who]: true };
    setSigs(next);
    await putAudit({ ...rec, answers, draftEvidence: draftEv, signatures: next });
    await refresh();
    if (navigator.onLine) sync();
    toast.success("Firma acquisita");
  };

  const submit = async () => {
    setBusy(true); setBlocked(null);
    try {
      const r = await sync();
      if (r && !r.ok) throw new Error("Non riesco a sincronizzare i dati: controlla la connessione e riprova.");
      await apiRequest(`/auditor/audits/${id}/start`, { method: "POST" }).catch(() => null);
      const res = await apiRequest(`/auditor/audits/${id}/submit`, { method: "POST" });
      toast.success(res.status === "bozza_pronta" ? "Audit inviato ed elaborato" : "Audit inviato");
      await putAudit({ ...rec, audit: { ...rec.audit, status: res.status, readonly: true } });
      navigate("/auditor");
    } catch (e) {
      let detail = null;
      try { detail = JSON.parse(e.message); } catch { /* messaggio semplice */ }
      setBlocked(detail?.blocked_by || [{ text: e.message }]);
      toast.error("Invio non riuscito: controlla l'elenco");
    } finally { setBusy(false); }
  };

  if (!rec) return <p className="p-6 text-center text-slate-600">{online ? "Caricamento…" : "Questo audit non è stato scaricato: collegati a internet almeno una volta."}</p>;
  const missing = ev.missing;
  const a = rec.audit;

  if (reviewMode) {
    return (
      <div className="space-y-4" data-testid="auditor-review">
        <Button variant="ghost" className="h-12" onClick={() => setReviewMode(false)}><ArrowLeft className="mr-1 h-5 w-5" /> Torna alla checklist</Button>
        <h1 className="font-heading text-2xl font-bold">Controlla e invia</h1>
        <section className="rounded-xl border bg-white p-4">
          <p className="font-semibold">{missing.length ? <span className="text-rose-800"><AlertTriangle className="mr-1 inline h-5 w-5" />Mancano {missing.length} risposte obbligatorie</span> : <span className="text-emerald-800"><CheckCircle2 className="mr-1 inline h-5 w-5" />Tutte le risposte obbligatorie sono state date</span>}</p>
          <ul className="mt-2 space-y-1 text-sm">
            {missing.slice(0, 40).map((k) => (
              <li key={k}><button className="text-left text-teal-900 underline" onClick={() => { setReviewMode(false); setTimeout(() => document.getElementById(`q-${k}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }}>{bundle.questions[ev.meta[k].question].text}</button></li>
            ))}
          </ul>
        </section>
        {blocked && <section className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4" data-testid="blocked-list"><p className="font-semibold">Il server segnala che manca:</p><ul className="list-disc pl-5 text-sm">{blocked.map((b, i) => <li key={i}>{b.text || String(b)}</li>)}</ul></section>}
        <section className="space-y-4 rounded-xl border bg-white p-4">
          <SignaturePad label="Firma dell'auditor (obbligatoria)" saved={sigs.auditor} onSave={(b) => saveSignature("auditor", b)} />
          <div className="space-y-2">
            <input className="h-12 w-full rounded-xl border-2 border-slate-300 px-3" placeholder="Nome del referente aziendale presente (facoltativo)" value={refName} onChange={(e) => setRefName(e.target.value)} />
            {refName && <SignaturePad label={`Firma di ${refName}`} saved={sigs.referente} onSave={(b) => saveSignature("referente", b)} />}
          </div>
        </section>
        {!online && <p className="rounded bg-amber-100 p-3 text-sm">Sei offline: puoi inviare solo con la connessione. I dati restano al sicuro sul dispositivo.</p>}
        <Button className="h-16 w-full text-lg" disabled={busy || !online || readonly || !sigs.auditor} onClick={submit} data-testid="submit-audit"><Send className="mr-2 h-5 w-5" /> {busy ? "Invio…" : "Invia audit"}</Button>
        <p className="text-center text-xs text-slate-600">Dopo l'invio la checklist diventa di sola lettura.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="auditor-checklist">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="photo-input" />
      <Link to="/auditor" className="inline-flex h-11 items-center text-teal-900"><ArrowLeft className="mr-1 h-5 w-5" /> I tuoi audit</Link>
      <div>
        <h1 className="font-heading text-2xl font-bold leading-tight">{a.company_name}</h1>
        <p className="flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-4 w-4" />{a.site_label} · {a.operating_address}</p>
      </div>
      {a.integration_request && <p className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3 text-sm"><b>Integrazione richiesta:</b> {a.integration_request.reason}</p>}
      {readonly && <p className="rounded-xl border bg-slate-100 p-3 text-sm font-medium">Audit inviato: la checklist è in sola lettura.</p>}
      <div className="rounded-xl border bg-white p-3">
        <div className="flex justify-between text-sm font-semibold"><span>Avanzamento</span><span>{done} / {total}</span></div>
        <div className="mt-1 h-3 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-teal-700 transition-all" style={{ width: `${total ? Math.round((100 * done) / total) : 0}%` }} /></div>
        <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5" checked={geoOn} onChange={(e) => { setGeoOn(e.target.checked); localStorage.setItem("dvr_geo", e.target.checked ? "1" : "0"); }} /> Registra la posizione negli scatti</label>
      </div>
      <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900"><AlertTriangle className="mr-1 inline h-4 w-4" />{rec.privacy_notice}</p>

      {grouped.map((g) => (
        <section key={g.task} className="space-y-3">
          <h2 className="sticky top-16 z-10 -mx-1 rounded-lg bg-teal-900 px-3 py-2 font-heading text-base font-bold text-white">{g.name}</h2>
          {g.keys.map((k) => {
            const q = bundle.questions[ev.meta[k].question];
            const eq = ev.meta[k].equipment;
            const it = answers[k];
            const nPhotos = (it?.evidence_ids || []).length;
            return (
              <article key={k} id={`q-${k}`} className={`space-y-3 rounded-2xl border-2 bg-white p-4 ${it ? "border-emerald-200" : q.required ? "border-slate-200" : "border-dashed border-slate-200"}`} data-testid="question">
                <p className="text-lg font-semibold leading-snug">{q.text}{eq && <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-sm font-medium">{bundle.equipment[eq]?.name || eq}</span>}{q.critical && <span className="ml-2 text-sm text-rose-700">● importante</span>}</p>
                {q.help && <p className="text-sm text-slate-600">{q.help}</p>}
                <AnswerControl q={q} item={it} disabled={readonly} onChange={(p) => save(k, p)} />
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" className="h-12 rounded-xl" disabled={readonly} onClick={() => takePhoto(k)} data-testid="take-photo"><Camera className="mr-2 h-5 w-5" /> Foto{nPhotos ? ` (${nPhotos})` : ""}</Button>
                  <Button type="button" variant="ghost" className="h-12" onClick={() => setOpenNote({ ...openNote, [k]: !openNote[k] })}>{openNote[k] || it?.note ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />} Nota</Button>
                  {q.evidence?.length > 0 && !nPhotos && <span className="text-xs text-slate-500">Consigliato: {q.evidence[0].label}</span>}
                </div>
                {(previews[k] || []).length > 0 && <div className="flex gap-2 overflow-x-auto">{previews[k].map((u) => <img key={u} src={u} alt="Foto scattata" className="h-20 w-20 rounded-lg object-cover" />)}</div>}
                {(openNote[k] || it?.note) && <textarea disabled={readonly} rows={2} maxLength={1000} className="w-full rounded-xl border-2 border-slate-300 p-3 text-base" placeholder="Nota (facoltativa)" defaultValue={it?.note || ""} onBlur={(e) => { if ((it?.note || "") !== e.target.value && it) save(k, { note: e.target.value }); }} />}
              </article>
            );
          })}
        </section>
      ))}

      <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-white p-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="min-w-0 flex-1 text-sm"><b>{done}/{total}</b> risposte{missing.length > 0 && <span className="text-rose-800"> · ne mancano {missing.length}</span>}</div>
          <Button className="h-14 rounded-xl px-6 text-base" onClick={() => setReviewMode(true)} data-testid="go-review"><ClipboardCheck className="mr-2 h-5 w-5" /> {readonly ? "Riepilogo" : "Controlla e invia"}</Button>
        </div>
      </div>
    </div>
  );
}
